/* eslint react/no-multi-comp: 0, react/prop-types: 0 */
import React from 'react';
import { Menu, type MenuProps } from 'antd';

import './SlabNav.scss';

// Owned slab-navigation primitive set, built on Ant Design's `Menu`. The SlabNav
// family is a vertical or horizontal "slab" of selectable items (icon over label,
// optional count/warning badge). It wraps AntD `Menu` so it reuses Menu's
// rendering, selection, and keyboard/ARIA behavior; the slab look (icon stacked
// over the label, active state, badges) is styled on top via owned tokens and a
// `slab-nav` className contract rather than the legacy GDS stylesheet. The family
// owns rendering and selection only — routing stays caller-owned.
//
// `src/ui` is extraction-clean (no `@app/*` imports), so the small class joiner
// below is inlined: strings concat, and object keys concat when their value is
// truthy.
type ClassArg = string | false | null | undefined | Record<string, unknown>;

const cx = (...args: ClassArg[]): string => {
    const classes: string[] = [];

    for (const arg of args) {
        if (!arg) continue;

        if (typeof arg === 'string') {
            classes.push(arg);
        } else if (typeof arg === 'object') {
            for (const [key, value] of Object.entries(arg)) {
                if (value) classes.push(key);
            }
        }
    }

    return classes.join(' ');
};

export type SlabNavContext =
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'quaternary';

export type SlabNavSize = 'xl' | 'lg' | 'sm' | 'xs';

/**
 * Visual tone of the slab:
 * - `legacy` (default) — the legacy `@relo` slab look: gray slab surface, white
 *   uppercase labels, and an active/hover fill in the app's brand colour (the
 *   runtime `--primary`, a muted purple by default) with the inset underline.
 *   This is the production drop-in.
 * - `brand` — the clean light/brand-tokened look (light surface, AntD Menu
 *   selected token styling). Opt in when a surface wants the modern variant.
 */
export type SlabNavTone = 'legacy' | 'brand';

export type SlabNavContainerProps = {
    children?: React.ReactNode;
    /** Justifies the child `SlabNavItem`s; also set `justify` on each item. */
    justify?: boolean;
    /** Stacks the child `SlabNavItem`s vertically; also set `vertical` on each item. */
    vertical?: boolean;
    /** Visual tone; defaults to `legacy` (the production drop-in look). */
    tone?: SlabNavTone;
    /** Legacy @relo dark surface. Prefer `tone`; kept for drop-in compatibility. */
    dark?: boolean;
    className?: string;
    style?: React.CSSProperties;
    /** Stable test hook forwarded to the underlying Menu root. */
    'data-test'?: string;
    'data-testid'?: string;
};

export type SlabNavItemProps = {
    /**
     * Stable key for the item. Defaults to `label` when omitted; supply an
     * explicit `itemKey` when labels can repeat or change.
     */
    itemKey?: React.Key;
    /**
     * Font Awesome v4 icon name (e.g. `copy fal`), rendered as `fa fa-fw fa-<icon>`.
     * Caller-owned icon string, matching the established family.
     */
    icon?: string;
    /** Text shown below the icon. */
    label?: string;
    /** Small numeric or short-text indicator. */
    count?: string | number;
    /** Adds a warning indicator to the item. */
    warning?: boolean;
    /** Marks the item selected and applies the active styling. */
    selected?: boolean;
    /** Disables the item (no click, dimmed). */
    disabled?: boolean;
    /** Justifies the item; pair with `justify` on the container. */
    justify?: boolean;
    /** Stacks the item vertically; pair with `vertical` on the container. */
    vertical?: boolean;
    /** Main color of the active/hover fill; defaults to the `primary` styling. */
    context?: SlabNavContext;
    /** Item size; defaults to the medium styling. */
    size?: SlabNavSize;
    /** Legacy @relo dark item modifier. Prefer `tone`; kept for drop-in compatibility. */
    dark?: boolean;
    /** Selection handler. The family owns selection only; routing stays caller-owned. */
    onClick?: React.MouseEventHandler<HTMLElement>;
    className?: string;
    style?: React.CSSProperties;
};

type MenuItem = NonNullable<MenuProps['items']>[number];

// `SlabNavItem` is a declarative config carrier: it renders nothing on its own.
// The container reads its props (via React.Children) to build AntD `Menu`'s
// `items`, so AntD owns the actual rendering/selection/keyboard handling while
// the consumer-facing JSX stays the familiar `<SlabNavContainer><SlabNavItem/>`.
const SlabNavItem = (_props: SlabNavItemProps): React.ReactElement | null =>
    null;

SlabNavItem.displayName = 'SlabNavItem';

const isSlabNavItem = (
    child: React.ReactNode,
): child is React.ReactElement<SlabNavItemProps> =>
    React.isValidElement(child) && child.type === SlabNavItem;

const renderItemLabel = (props: SlabNavItemProps): React.ReactNode => {
    const { icon, label, count, warning, size } = props;

    const iconClasses = cx('slab-nav__item-icon', 'fa', 'fa-fw', {
        [`slab-nav__item-icon--${size}`]: size,
        [`fa-${icon}`]: icon,
    });
    const labelClasses = cx('slab-nav__item-label', {
        [`slab-nav__item-label--${size}`]: size,
    });

    return (
        <span className="slab-nav__item-content">
            {icon && <i className={iconClasses} aria-hidden="true" />}
            {label && <h4 className={labelClasses}>{label}</h4>}
            {(count || warning) && (
                <span className="slab-nav__item-indicator">
                    {count !== undefined && count !== '' && (
                        <span className="slab-nav__item-badge">{count}</span>
                    )}
                    {warning && (
                        <i
                            className="slab-nav__item-warning fa fa-fw fa-exclamation-circle"
                            aria-hidden="true"
                        />
                    )}
                </span>
            )}
        </span>
    );
};

const SlabNavContainer = ({
    children,
    justify,
    vertical,
    tone = 'legacy',
    dark,
    className,
    style,
    ...rest
}: SlabNavContainerProps): React.ReactElement => {
    const itemElements = React.Children.toArray(children).filter(isSlabNavItem);

    const selectedKeys: string[] = [];

    const items: MenuItem[] = itemElements.map((element, index) => {
        const props = element.props;
        const key = String(props.itemKey ?? props.label ?? index);

        if (props.selected) selectedKeys.push(key);

        const itemClassName = cx(
            'slab-nav__item',
            {
                [`slab-nav__item--${props.context ?? 'primary'}`]: true,
                [`slab-nav__item--${props.size}`]: props.size,
                'slab-nav__item--active': props.selected,
                'slab-nav__item--justify': props.justify,
                'slab-nav__item--vertical': props.vertical,
                'slab-nav__item--dark': props.dark ?? dark,
            },
            props.className,
        );

        return {
            key,
            label: renderItemLabel(props),
            disabled: props.disabled,
            className: itemClassName,
            style: props.style,
            title: props.label,
            onClick: props.onClick
                ? ({ domEvent }) =>
                      props.onClick?.(domEvent as React.MouseEvent<HTMLElement>)
                : undefined,
        };
    });

    const containerClasses = cx(
        'slab-nav',
        `slab-nav--${tone}`,
        {
            'slab-nav--vertical': vertical,
            'slab-nav--justify': justify,
            'slab-nav--dark': dark,
        },
        className,
    );

    return (
        <Menu
            {...rest}
            className={containerClasses}
            style={style}
            mode={vertical ? 'inline' : 'horizontal'}
            // Slab items are centered, so drop AntD's inline left indent.
            inlineIndent={0}
            // The legacy slab look is a custom dark surface, so its colour is
            // owned by SlabNav.scss rather than AntD's dark menu theme; keep
            // AntD on the light theme and let the tone class drive the surface.
            theme="light"
            // Selection stays caller-owned: each item drives its own `selected`
            // prop, so the Menu reflects state rather than mutating it.
            selectable={false}
            selectedKeys={selectedKeys}
            items={items}
        />
    );
};

SlabNavContainer.displayName = 'SlabNavContainer';

export { SlabNavContainer, SlabNavItem };
